// @ts-strict-ignore
import useAppChannel from "@dashboard/components/AppLayout/AppChannelContext";
import { useAnalyticsData } from "@dashboard/welcomePage/analytics/hooks/useAnalyticsData";
import { useAnalyticsFilters } from "@dashboard/welcomePage/analytics/hooks/useAnalyticsFilters";
import React from "react";

import { ChartRenderer } from "./chartRenderer";
import { parseIntentToChartSpec } from "./intent";
import { ChartSpec } from "./mockData";

type ChatMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
  chartSpec?: ChartSpec;
};

const WsmAiSection: React.FC = () => {
  const [messages, setMessages] = React.useState<ChatMessage[]>([]);
  const [input, setInput] = React.useState("");
  const listRef = React.useRef<HTMLDivElement | null>(null);

  // Align data source with Analytics Dashboard
  const { channel } = useAppChannel(false);
  const { dateRange } = useAnalyticsFilters();
  const { data: analyticsData } = useAnalyticsData(channel?.id, dateRange);

  // Dummy staged statuses
  const THINKING = "Thinking…";
  const GENERATING = "Generating…";
  const rand = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;

  const stagedAssistantReply = React.useCallback(
    (prompt: string) => {
      // 1) Add placeholder assistant message with "Thinking…"
      const placeholderId = cryptoRandomId();

      setMessages(prev => [...prev, { id: placeholderId, role: "assistant", content: THINKING }]);

      // 2) After short delay, switch to "Generating…"
      const thinkingDelay = rand(1500, 3000);
      const generatingDelay = rand(2000, 4000);

      setTimeout(() => {
        setMessages(prev =>
          prev.map(m => (m.id === placeholderId ? { ...m, content: GENERATING } : m)),
        );

        // 3) After another delay, compute final spec and replace message
        setTimeout(() => {
          const spec = parseIntentToChartSpec(prompt, analyticsData);

          setMessages(prev =>
            prev.map(m =>
              m.id === placeholderId
                ? spec
                  ? { ...m, content: "", chartSpec: spec }
                  : { ...m, content: `This is the text that was entered: ${prompt}` }
                : m,
            ),
          );
        }, generatingDelay);
      }, thinkingDelay);
    },
    [analyticsData],
  );

  // Seed from query param `q` once analytics data is ready
  const seededRef = React.useRef(false);

  React.useEffect(() => {
    if (seededRef.current) return;

    const params = new URLSearchParams(window.location.search);
    const q = params.get("q")?.trim();

    if (!q) return;

    const userMsg: ChatMessage = { id: cryptoRandomId(), role: "user", content: q };

    setMessages([userMsg]);
    stagedAssistantReply(q);
    seededRef.current = true;
  }, [analyticsData]);

  // Auto-scroll to latest
  React.useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages.length]);

  const send = () => {
    const text = input.trim();

    if (!text) return;

    const userMsg: ChatMessage = { id: cryptoRandomId(), role: "user", content: text };

    setMessages(prev => [...prev, userMsg]);
    setInput("");
    // staged reply: Thinking… -> Generating… -> Final
    stagedAssistantReply(text);
  };

  // Suggestion logic
  const getSuggestions = React.useCallback((): string[] => {
    // Basic heuristics based on last AI chart shown or general analytics
    const last = [...messages].reverse().find(m => m.role === "assistant");

    if (last?.chartSpec?.title?.toLowerCase().includes("sales")) {
      return [
        "Break down sales by category",
        "Show sales by channel",
        "What’s the average order value?",
      ];
    }

    if (last?.chartSpec?.title?.toLowerCase().includes("refund")) {
      return [
        "What is the refund rate?",
        "How do refunds compare to cancellations?",
        "Show refunds trend for the last 30 days",
      ];
    }

    if (last?.chartSpec?.title?.toLowerCase().includes("low stock")) {
      return [
        "Show top 10 low-stock variants",
        "Any out-of-stock SKUs?",
        "What is the low stock threshold?",
      ];
    }

    // Generic fallbacks
    return ["Show sales over time", "Top 5 best-selling products", "Orders by status"];
  }, [messages]);

  const sendSuggestion = (q: string) => {
    setInput(q);

    // mirror send() but using provided q
    const text = q.trim();

    if (!text) return;

    const userMsg: ChatMessage = { id: cryptoRandomId(), role: "user", content: text };

    setMessages(prev => [...prev, userMsg]);
    setInput("");
    stagedAssistantReply(text);
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: "calc(100vh - 64px)",
        background: "#FAFBFF", // light app background
        color: "#111827",
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "14px 20px",
          borderBottom: "1px solid #E5E7EB",
          background: "#FFFFFF",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ fontSize: 20, fontWeight: 700 }}>WSM AI</div>
        <div style={{ fontSize: 12, color: "#6B7280" }}>Light preview</div>
      </div>

      {/* Messages */}
      <div
        ref={listRef}
        style={{
          flex: 1,
          width: "100%",
          margin: "0 auto",
          maxWidth: 960,
          overflowY: "auto",
          padding: 24,
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        {messages.length === 0 ? (
          <div
            style={{
              margin: "64px auto",
              textAlign: "center",
              color: "#6B7280",
              maxWidth: 640,
              lineHeight: 1.6,
            }}
          >
            Start by asking a question below. If you came from the welcome page, your question will
            appear here automatically.
          </div>
        ) : (
          <>
            {/* Show first two seeded messages in styled blocks, then any additional chat */}
            {messages.map(m => (
              <div
                key={m.id}
                style={{
                  display: "flex",
                  justifyContent: m.role === "user" ? "flex-end" : "flex-start",
                }}
              >
                <div style={m.role === "user" ? userBubbleStyle : assistantBubbleStyle}>
                  {m.chartSpec ? (
                    <div style={{ width: 640 }}>
                      <ChartRenderer spec={m.chartSpec} width={640} height={220} />
                    </div>
                  ) : m.role === "assistant" &&
                    (m.content === THINKING || m.content === GENERATING) ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <Spinner />
                      <div
                        style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 16 }}
                      >
                        {m.content}
                      </div>
                    </div>
                  ) : (
                    <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 16 }}>
                      {m.content}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Composer */}
      <div
        style={{
          padding: 16,
          // borderTop: "1px solid #E5E7EB",
          // background: "#FFFFFF",
        }}
      >
        <div style={{ maxWidth: 960, margin: "0 auto" }}>
          {/* Suggestions */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
            {getSuggestions().map(s => (
              <button
                key={s}
                onClick={() => sendSuggestion(s)}
                style={{
                  border: "1px solid #E5E7EB",
                  background: "#F9FAFB",
                  color: "#111827",
                  borderRadius: 999,
                  padding: "6px 12px",
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                {s}
              </button>
            ))}
          </div>
          <div style={composerOuterStyle}>
            <IconButton ariaLabel="Add">
              <PlusIcon />
            </IconButton>

            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder="Ask anything"
              rows={1}
              style={{
                flex: 1,
                border: "none",
                outline: "none",
                background: "transparent",
                fontSize: 16,
                lineHeight: 1.5,
                padding: "10px 0",
                resize: "none",
              }}
            />

            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <IconButton ariaLabel="Voice">
                <MicIcon />
              </IconButton>
              <button
                type="button"
                onClick={send}
                aria-label="Send message"
                disabled={!input.trim()}
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 18,
                  border: "none",
                  background: input.trim() ? "#111827" : "#D1D5DB",
                  color: "#FFFFFF",
                  cursor: input.trim() ? "pointer" : "default",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ArrowIcon />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const userBubbleStyle: React.CSSProperties = {
  maxWidth: 760,
  padding: "10px 14px",
  borderRadius: 16,
  background: "#E5E7EB",
  color: "#111827",
  fontSize: 15,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};

const assistantBubbleStyle: React.CSSProperties = {
  maxWidth: 760,
  padding: "14px 16px",
  borderRadius: 16,
  background: "#FFFFFF",
  color: "#111827",
  fontSize: 16,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  border: "1px solid #E5E7EB",
  boxShadow: "0 1px 2px rgba(0,0,0,.04)",
};

const composerOuterStyle: React.CSSProperties = {
  border: "1px solid #E5E7EB",
  background: "#FFFFFF",
  borderRadius: 9999,
  padding: "6px 10px",
  display: "flex",
  alignItems: "center",
  gap: 8,
};

const IconButton: React.FC<{ ariaLabel: string; children: React.ReactNode }> = ({
  ariaLabel,
  children,
}) => (
  <button
    type="button"
    aria-label={ariaLabel}
    style={{
      width: 32,
      height: 32,
      borderRadius: 16,
      border: "1px solid #E5E7EB",
      background: "#FFFFFF",
      color: "#6B7280",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
    }}
  >
    {children}
  </button>
);

// (Removed AssistantTemplate and CodeInline as we now always render the entered text message.)

// const ActionsBar: React.FC = () => (
//   <div style={{ display: "flex", gap: 10, color: "#6B7280" }}>
//     <SmallIconButton ariaLabel="Copy"><CopyIcon /></SmallIconButton>
//     <SmallIconButton ariaLabel="Thumbs up"><ThumbUpIcon /></SmallIconButton>
//     <SmallIconButton ariaLabel="Thumbs down"><ThumbDownIcon /></SmallIconButton>
//     <SmallIconButton ariaLabel="Audio"><AudioIcon /></SmallIconButton>
//     <SmallIconButton ariaLabel="Refresh"><RefreshIcon /></SmallIconButton>
//   </div>
// );

// Inline SVG icons (no external libraries)
const Spinner: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ color: "#6B7280" }}
  >
    {/* static track */}
    <circle cx="12" cy="12" r="9" strokeOpacity="0.2" />
    {/* rotating arc */}
    <g>
      <path d="M21 12a9 9 0 0 0-9-9" />
      <animateTransform
        attributeName="transform"
        type="rotate"
        from="0 12 12"
        to="360 12 12"
        dur="0.9s"
        repeatCount="indefinite"
      />
    </g>
  </svg>
);

const PlusIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const MicIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 1a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
    <path d="M19 10a7 7 0 0 1-14 0" />
    <path d="M12 19v4" />
  </svg>
);

const ArrowIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M5 12h14" />
    <path d="M13 5l7 7-7 7" />
  </svg>
);

function cryptoRandomId() {
  // Fallback to Math.random if crypto not available
  if (typeof crypto !== "undefined" && "getRandomValues" in crypto) {
    const arr = new Uint32Array(2);

    crypto.getRandomValues(arr);

    return Array.from(arr)
      .map(n => n.toString(36))
      .join("");
  }

  return Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
}

export default WsmAiSection;
